import React, { useEffect, useRef, useState, useContext } from 'react';
import axios from '../common/axios';
import { Button, Alert } from 'antd';
import MathJaxHTMLContent from "../common/MathJaxHTMLContent";
import CircularLoader from "../common/CircularLoader";
import WalkthroughStep from "./WalkthroughStep";
import { BrandContext } from "../common/BrandContext";

const TrailWalkthrough = ({ step, stepActivity, showResourceIds = false, continueCurrentStep }) => {
    const brandConfig = useContext(BrandContext);
    const [question, setQuestion] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [showStep, setShowStep] = useState(false);
    const [showSummary, setShowSummary] = useState(false);

    const summaryRef = useRef();
    const instructionsRef = useRef();

    useEffect(() => {
        let isActive = true;

        if (!step || !stepActivity) {
            if (isActive) setLoading(false);
            return;
        }

        const loadWalkthrough = async () => {
            setLoading(true);
            setError(undefined);

            try {
                const response = await axios.get('/web/ui/walkthrough/trail/walkthrough', {
                    params: { questionId: stepActivity.questionId, conceptId: step.conceptId }
                });
                if (isActive) {
                    setQuestion(response.data);
                }
            } catch (err) {
                if (isActive) {
                    setError('Walkthrough not found');
                    console.error(err);
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        loadWalkthrough();

        return () => { isActive = false; };
    }, [step, stepActivity]);

    useEffect(() => {
        if (showSummary && summaryRef.current) {
            summaryRef.current.focus();
        }
    }, [showSummary]);

    useEffect(() => {
        if (!showSummary && !showStep && instructionsRef.current) {
            instructionsRef.current.focus();
        }
    }, [showStep, showSummary]);

    if (loading) return <CircularLoader role="status" aria-live="polite" />;
    if (error) return <Alert type="error" message={error} role="alert" aria-live="assertive" />;

    if (!question) return null;

    return (
        <main className="ch-trail-walkthrough flexColumn mediumGap" aria-labelledby="walkthrough-title">
            {!showSummary && (
                <>
                    {showResourceIds && (
                        <span className="resourceId">Question: {stepActivity.questionId}</span>
                    )}

                    <h3 id="walkthrough-title" tabIndex="-1" ref={instructionsRef}>
                        {brandConfig.walkthroughName} {question.walkthroughIndex}
                    </h3>

                    <MathJaxHTMLContent
                        html={question.formattedInstructions}
                        showLoader={false}
                    />
                    <MathJaxHTMLContent
                        html={question.initialDisplay}
                        showLoader={false}
                    />

                    {!showStep && (
                        <div
                            className="flexRow flexAlignCentre smallGap"
                            role="group"
                            aria-label="Walkthrough navigation buttons"
                        >
                            <Button
                                type="primary"
                                onClick={() => setShowStep(true)}
                                aria-label="Begin the walkthrough"
                            >
                                Begin
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => setShowSummary(true)}
                                aria-label="View the summary of the walkthrough"
                            >
                                Summary
                            </Button>
                        </div>
                    )}
                </>
            )}

            {showStep && !showSummary && (
                <WalkthroughStep
                    step={step}
                    stepActivity={stepActivity}
                    aria-label="Walkthrough step"
                    onBack={() => setShowStep(false)}
                    continueCurrentStep={continueCurrentStep}
                    setShowSummary={setShowSummary}
                />
            )}

            {showSummary && (
                <section
                    aria-labelledby="summary-title"
                    aria-live="polite"
                    className="flexColumn mediumGap"
                    tabIndex="-1"
                    ref={summaryRef}
                >
                    <h3 id="summary-title">Summary</h3>
                    <MathJaxHTMLContent html={question.summary} showLoader={false} />

                    <div className="flexRow flexJustifyStart">
                        <Button
                            type="primary"
                            onClick={() => setShowSummary(false)}
                            aria-label="Back to walkthrough instructions"
                        >
                            Back
                        </Button>
                    </div>
                </section>
            )}
        </main>
    );
};

export default TrailWalkthrough;